import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Phone, CalendarCheck, Mail, MapPin } from "lucide-react";
import { useSettings } from "../lib/settings";
import { track, getTagConfig } from "../lib/analytics";
import { openCookiePreferences } from "../lib/consent";
import { BRAND } from "../config";

const COLS = [
  {
    title: "Servizi",
    links: [
      ["Privati", "/privati-famiglie"],
      ["Pensioni", "/pensioni-previdenza"],
      ["Casa", "/casa-locazioni"],
      ["Lavoro domestico", "/lavoro-domestico"],
      ["Partite IVA", "/partite-iva-professionisti"],
      ["Imprese", "/imprese-societa"],
      ["Servizi digitali", "/servizi-digitali"],
      ["Assistenza notarile", "/notarile"],
      ["Stranieri in Italia", "/servizi-stranieri-italia"],
    ],
  },
  {
    title: "Assistenza",
    links: [
      ["Contatti", "/contatti"],
      ["Prenota appuntamento", "/prenota"],
      ["Strumenti gratuiti", "/strumenti"],
      ["Tutti i servizi", "/servizi"],
      ["Domande frequenti", "/#faq"],
      ["Chi siamo", "/chi-siamo"],
    ],
  },
  {
    title: "Servizi a Roma",
    links: [
      ["730 a Roma", "/730-roma"],
      ["ISEE a Roma", "/isee-roma"],
      ["Successioni a Roma", "/successioni-roma"],
      ["Registrazione locazioni a Roma", "/registrazione-contratto-locazione-roma"],
      ["Colf e badanti a Roma", "/colf-badanti-roma"],
      ["SPID a Roma", "/spid-roma"],
      ["Firma digitale a Roma", "/firma-digitale-roma"],
      ["FLASH a Roma (hub)", "/roma"],
      ["Roma Sud", "/roma-sud"],
      ["EUR e Torrino", "/eur-torrino"],
      ["Infernetto, Casal Palocco e Axa", "/infernetto-casal-palocco-axa"],
      ["Ostia", "/ostia"],
      ["Acilia", "/acilia"],
    ],
  },
  {
    title: "Legale",
    links: [
      ["Privacy Policy", "/privacy"],
      ["Cookie Policy", "/cookie"],
      ["Termini e Condizioni", "/termini"],
      ["Condizioni di Vendita", "/condizioni-vendita"],
      ["Termini FLASH Plus", "/termini-flash-plus"],
      ["Recesso e Rimborsi", "/rimborsi"],
    ],
  },
];

export const Footer = () => {
  const { settings, waLink, hasPhone, phoneHref } = useSettings();
  const [gbpUrl, setGbpUrl] = useState("");
  useEffect(() => {
    getTagConfig().then((c) => setGbpUrl(c.googleBusinessUrl || "")).catch(() => {});
  }, []);
  const wa = waLink();
  const address = settings
    ? [settings.address, [settings.postalCode, settings.city, settings.province && `(${settings.province})`].filter(Boolean).join(" ")].filter(Boolean).join(", ")
    : "";

  return (
    <footer data-testid="site-footer" className="bg-[#171717] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-8">
        <div className="grid md:grid-cols-[1.3fr_1fr_1fr_1fr] gap-10 pb-12 border-b border-white/10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src="/flash-icon.png" alt="FLASH" width="40" height="40" loading="lazy" className="h-10 w-10" />
              <div className="font-display font-bold text-lg leading-tight">
                FLASH<br />
                <span className="text-[#F26522] text-sm font-semibold">Service &amp; Consulting</span>
              </div>
            </div>
            <p className="text-sm text-white/60 leading-relaxed max-w-xs mb-6">{BRAND.concept}</p>
            <div className="space-y-2 text-sm text-white/70">
              {settings?.legalName && <div data-testid="footer-legal-name" className="font-semibold text-white/90">{settings.legalName}</div>}
              {settings?.vatNumber && <div data-testid="footer-vat">P.IVA / C.F. {settings.vatNumber}</div>}
              {address && <div>{address}</div>}
              {settings?.pec && <div data-testid="footer-pec">PEC: {settings.pec}</div>}
              {settings?.email && <div>{settings.email}</div>}
            </div>
            <div className="flex gap-3 mt-6">
              {wa && (
                <a data-testid="footer-whatsapp-link" href={wa} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { placement: "footer" })} aria-label="WhatsApp" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#F26522] flex items-center justify-center transition-colors">
                  <MessageCircle size={17} />
                </a>
              )}
              {hasPhone && (
                <a data-testid="footer-phone-link" href={phoneHref} onClick={() => track("phone_click", { placement: "footer" })} aria-label="Chiama" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#F26522] flex items-center justify-center transition-colors">
                  <Phone size={17} />
                </a>
              )}
              {settings?.email && (
                <a data-testid="footer-email-link" href={`mailto:${settings.email}`} aria-label="Email" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#F26522] flex items-center justify-center transition-colors">
                  <Mail size={17} />
                </a>
              )}
              <Link data-testid="footer-book-link" to="/prenota" aria-label="Prenota" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#F26522] flex items-center justify-center transition-colors">
                <CalendarCheck size={17} />
              </Link>
              <a
                data-testid="footer-google-link"
                href={gbpUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`FLASH Service & Consulting, ${address || "Via Francesco Cilea 7, 00124 Roma"}`)}`}
                target="_blank" rel="noopener noreferrer" aria-label="Trovaci su Google"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#F26522] flex items-center justify-center transition-colors"
              >
                <MapPin size={17} />
              </a>
            </div>
          </div>
          {COLS.map((col) => (
            <div key={col.title}>
              <div className="text-xs font-semibold uppercase tracking-wider text-[#FFC400] mb-4">{col.title}</div>
              <ul className="space-y-2.5">
                {col.links.map(([label, to]) => (
                  <li key={label}>
                    {to.startsWith("/#") ? (
                      <Link to="/" onClick={() => setTimeout(() => document.querySelector(to.slice(1))?.scrollIntoView({ behavior: "smooth" }), 100)} data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="text-sm text-white/70 hover:text-white transition-colors">
                        {label}
                      </Link>
                    ) : (
                      <Link to={to} data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="text-sm text-white/70 hover:text-white transition-colors">
                        {label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="pt-7 flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs text-white/40">
            © {new Date().getFullYear()} {settings?.legalName || BRAND.name} — Tutti i diritti riservati
          </span>
          <button
            data-testid="footer-cookie-preferences"
            onClick={() => openCookiePreferences()}
            className="text-xs text-white/40 hover:text-white transition-colors underline underline-offset-2"
          >
            Preferenze cookie
          </button>
          <span className="font-display italic text-sm text-[#FFC400]/90">“{BRAND.tagline}”</span>
        </div>
      </div>
    </footer>
  );
};
